import LegalLayout from '@/layouts/LegalLayout';
import { usePublicCoinPacks } from '@/lib/data/wallet';
import { formatRupees } from '@/lib/format';

/**
 * Razorpay requires a page stating pricing before it will activate a merchant
 * account. It also happens to be the page people look for before signing up.
 */
export default function Pricing() {
  /*
   * The real price list, read from the same table the wallet charges against.
   *
   * This page used a hardcoded copy. A statutory pricing page that can drift
   * from what is actually charged is the one place a stale constant is not a
   * cosmetic problem — Razorpay reads this page during activation, and a
   * customer can reasonably hold us to whatever it says.
   */
  const packs = usePublicCoinPacks();

  return (
    <LegalLayout title="Pricing" updated="draft">
      <p>
        <strong>No subscription.</strong> You buy coins once and spend them a minute at a time. You
        are charged by the second, and unused time returns to your balance.
      </p>

      <h2>Session rates</h2>
      <p>Rates are in coins per minute, and vary by companion. Indicative starting rates:</p>
      <ul>
        <li>
          <strong>Chat</strong> — from 6 coins per minute
        </li>
        <li>
          <strong>Voice</strong> — from 14 coins per minute
        </li>
        <li>
          <strong>Video</strong> — from 26 coins per minute
        </li>
      </ul>
      <p>
        The exact rate for a companion is shown on their profile and again before a session starts.
        It is fixed for the whole of that session — it cannot change while you are talking.
      </p>

      <h2>Coin packs</h2>
      {packs.length === 0 ? (
        <p>Loading the current price list…</p>
      ) : (
        <ul>
          {packs.map((p) => (
            <li key={p.code}>
              <strong>{formatRupees(p.price_paise)}</strong> —{' '}
              {p.coins.toLocaleString('en-IN')} coins
              {p.bonus_coins > 0 && (
                <> plus {p.bonus_coins.toLocaleString('en-IN')} bonus coins</>
              )}
            </li>
          ))}
        </ul>
      )}
      <p>
        All prices are in Indian Rupees and include applicable taxes.{' '}
        <strong>[CONFIRM GST TREATMENT WITH YOUR CA]</strong> — decide whether prices are shown
        inclusive or exclusive of GST, and state it here explicitly.
      </p>

      <h2>What you are not charged for</h2>
      <ul>
        <li>Creating an account, or browsing companions</li>
        <li>A session that fails to connect</li>
        <li>Time after you end a session — billing stops the second it does</li>
        <li>Any recurring or hidden fee. There are none.</li>
      </ul>

      <h2>Bonus coins</h2>
      <p>
        Promotional coins are spent before purchased coins, expire <strong>90 days</strong> after
        being issued, and are not refundable.
      </p>

      <h2>Refunds</h2>
      <p>
        See the <a href="/refunds">Cancellation and refunds policy</a>.
      </p>

      <p>
        <strong>[BEFORE LAUNCH]</strong> — these rates are illustrative. Replace with the final rate
        card once it is agreed, and keep this page in sync with what the app actually charges. A
        mismatch here is a compliance problem, not a copy problem.
      </p>
    </LegalLayout>
  );
}
